import { ROOT_ROUTES } from '../configurations/router.config';
import {
  ELhAppRoles,
  EWrContentType,
  EWrReportType,
  IExagoFolderNode,
  REPORTING_REPORT_TYPES,
} from '../../feature-modules/exago/models/exago.models';
import {
  EDateFilterType,
  IButtonConfig,
  IMenuItem,
  INavItem,
  MenuItemModel,
} from '../../layout/navigation/models/nav-item.model';
import { Inject, Injectable } from '@angular/core';
import { ClaimsService } from './index';
import { NAVIGATION_TOKEN } from '../configurations/navigation/navigation.config';
import { firstValueFrom, map } from 'rxjs';
import { EClaimsGroup, TApplicationClaimMapKey } from '../configurations/claims.config';
import { Store } from '@ngrx/store';
import { AUTH_SELECTORS } from '../store/selectors/auth.selectors';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  static readonly exagoAppsUrls: ReadonlyArray<ROOT_ROUTES> = [ROOT_ROUTES.dashboard, ROOT_ROUTES.reporting];
  static readonly hiddenPageNamePart: string = '[Hidden]';
  static readonly monthVSMonthFilterNamePart: string = '[filter = mvm]';
  static readonly scorecardFilterNamePart: string = '[Scorecard Filter]';

  constructor(
    private readonly claimService: ClaimsService,
    @Inject(NAVIGATION_TOKEN) private readonly appNavigationTemplate: INavItem[],
    private readonly store: Store,
  ) {}

  async getNavigation(folderNode: IExagoFolderNode[]): Promise<INavItem[]> {
    const resultNavigation: INavItem[] = [];

    for (let i = 0; i < this.appNavigationTemplate.length; i++) {
      const navItem: INavItem = structuredClone(this.appNavigationTemplate[i]);
      const hasExternalLink: boolean = navItem.url.includes('http');
      const claimAppKey: TApplicationClaimMapKey | null = this.claimService.getApplicationMapKeyFromURL(navItem.url);
      const canAppBeShown: boolean =
        hasExternalLink || (await firstValueFrom(this.claimService.hasApplicationReadClaims(claimAppKey)));
      if (canAppBeShown) await this.updateNavigation(resultNavigation, navItem, folderNode);
      navItem.buttons = await this.getNavItemButtons(navItem.buttons);
    }

    return resultNavigation;
  }

  private async getNavItemButtons(buttons: IButtonConfig[]): Promise<IButtonConfig[]> {
    const buttonsVisibility$ = buttons.map((button): Promise<boolean> => this.checkButtonVisibility(button));
    const buttonsVisibilityData = await Promise.all(buttonsVisibility$);
    return buttons.filter((button: IButtonConfig, index: number) => buttonsVisibilityData[index]);
  }

  private async checkButtonVisibility(button: IButtonConfig): Promise<boolean> {
    if ((button.url as ROOT_ROUTES) === ROOT_ROUTES.newItem) {
      return await firstValueFrom(
        this.store
          .select(AUTH_SELECTORS.exagoRole)
          .pipe(map((role: string) => (role as ELhAppRoles) === ELhAppRoles.ExagoAllAccess)),
      );
    }
    return true;
  }

  private async updateNavigation(
    navigation: INavItem[],
    navItem: INavItem,
    folderNode: IExagoFolderNode[],
  ): Promise<void> {
    if (navItem.standalone) {
      navigation.push(navItem);
      return;
    }

    if (NavigationService.exagoAppsUrls.includes(navItem.url as ROOT_ROUTES)) {
      navItem.subNav.items = NavigationService.getGroupItems(folderNode, navItem.url, true);
    } else {
      navItem.subNav.items = await this.getFilteredNavigationGroups(navItem.subNav.items as IMenuItem[]);
    }
    if (navItem.subNav.items.length) {
      navigation.push(navItem);
    }
  }

  private async getFilteredNavigationGroups(groups: IMenuItem[]): Promise<IMenuItem[]> {
    const result: IMenuItem[] = [];

    for (let i = 0; i < groups.length; i++) {
      const hasClaims: boolean = await firstValueFrom(
        this.claimService.isGroupHasReadClaims(groups[i].claimGroupName as EClaimsGroup),
      );
      if (!hasClaims) continue;
      const group: IMenuItem = Object.assign({}, groups[i]);
      group.items = await this.getFilteredPageNavigationItemsByClaims(group.items as IMenuItem[]);
      if (group.items.length) {
        result.push(group);
      }
    }

    return result;
  }

  private async getFilteredPageNavigationItemsByClaims(pages: IMenuItem[]): Promise<IMenuItem[]> {
    const result: IMenuItem[] = [];

    for (let i = 0; i < pages.length; i++) {
      if (pages[i]?.items?.length) {
        const groups = await this.getFilteredNavigationGroups([pages[i]]);
        groups.length && result.push(groups[0]);
      } else {
        const hasClaims: boolean = await firstValueFrom(this.claimService.hasPageClaim(pages[i].claimName as string));
        hasClaims && result.push(pages[i]);
      }
    }

    return result;
  }

  private static getGroupItems(
    folderNode: IExagoFolderNode[] = [],
    parentPath: string = '',
    skipCreatingGroup: boolean = false,
  ): IMenuItem[] {
    let groups: IMenuItem[] = [];
    folderNode
      .filter((node: IExagoFolderNode) => !node.name.includes(this.hiddenPageNamePart))
      .forEach((node: IExagoFolderNode) => {
        if (node.content_type === EWrContentType.Folder && node.entity?.length) {
          const children: IMenuItem[] = this.getGroupItems(
            node.entity,
            parentPath ? `${parentPath}/${node.name}` : node.name,
          );
          if (children.length && !skipCreatingGroup) {
            groups.push(this.getGroup(node, children));
          } else if (children.length) {
            groups = [...groups, ...children];
          }
        } else if (node.content_type === EWrContentType.Report) {
          this.pushReportToGroup(parentPath, node, groups);
        }
      });

    return groups;
  }

  private static getGroup(node: IExagoFolderNode, children: IMenuItem[]): MenuItemModel {
    return new MenuItemModel({
      label: node.name,
      items: children,
    });
  }

  private static getPage({ name, type }: IExagoFolderNode, parentPath: string = ''): MenuItemModel {
    const dateFilterType = name.includes(this.monthVSMonthFilterNamePart)
      ? EDateFilterType.monthVsMonth
      : EDateFilterType.period;
    const label = this.getReportName(name);
    const showScorecardFilter = Boolean(name.includes(this.scorecardFilterNamePart));

    return new MenuItemModel({
      label,
      routerLink: `${parentPath}/${name}`,
      data: { type, dateFilterType, showScorecardFilter },
    });
  }

  private static getReportName(name: string): string {
    return name.replace(this.monthVSMonthFilterNamePart, '').replace(this.scorecardFilterNamePart, '').trim();
  }

  private static pushReportToGroup(parentPath: string, node: IExagoFolderNode, parentGroups: IMenuItem[]): void {
    const isReportingNode =
      parentPath.startsWith(ROOT_ROUTES.reporting) && node.type && REPORTING_REPORT_TYPES.includes(node.type);
    const isDashboardNode = parentPath.startsWith(ROOT_ROUTES.dashboard) && node.type === EWrReportType.Dashboard;
    if (isReportingNode || isDashboardNode) {
      parentGroups.push(this.getPage(node, parentPath));
    }
  }
}
